import React from 'react';
import PropTypes from 'prop-types';
import {
  StripeProvider,
  Elements,
  injectStripe
} from 'react-stripe-elements';
import Placeholder from '../Placeholder';
import { CustomerType } from '../../propTypes';
import { duckify, currentCustomer } from '../../utils/duckify';
import requireScript from '../../utils/requireScript';
import rollbar from '../../utils/rollbarLogger';


// token used for PaymentIntent/SetupIntent
const DUMMY_TOKEN = { id: 'ignore' };

// Errors that should be counted as info when sending to Rollbar
const IGNORE_STRIPE_ERRORS = [
  'card_error'
];

const logStripeError = (error, token) => {
  const logger = IGNORE_STRIPE_ERRORS.includes(error.type) ? rollbar.info : rollbar.error;

  logger(`Error submitting card info to Stripe: ${error.type} : ${error.message}`, {error, token});
};

class InjectableForm extends React.Component {
  handleError(error, token) {
    const {
      onError = ()=>{}
    } = this.props;

    logStripeError(error, token);
    onError(error);
  }

  handleSubmit(ev) {
    const {
      onSubmit = ()=>{},
      beforeSubmit = ()=>{},
      details = {},
      usesIntents = false,
      stripe,
      elements
    } = this.props;

    const createToken = () => {
      return usesIntents
        ? Promise.resolve({ token: DUMMY_TOKEN })
        : stripe.createToken(details);
    };

    ev.preventDefault();

    beforeSubmit();

    createToken().then(({token, error}) => {
      if(error) {
        this.handleError(error);
      }
      else {
        onSubmit(token, {stripe, elements});
      }
    }).catch(this.handleError.bind(this));
  }

  render() {
    return <form onSubmit={this.handleSubmit.bind(this)}>
      {this.props.children}
    </form>;
  }
}

const InjectedForm = injectStripe(InjectableForm);

class StripeForm extends React.Component {
  static propTypes = {
    allDucksReady: PropTypes.bool,
    customer: CustomerType.isRequired,
    usesIntents: PropTypes.bool,
    children: PropTypes.element.isRequired
  };

  static defaultProps = {
    allDucksReady: false,
    usesIntents: false
  };

  render() {
    const {
      allDucksReady,
      customer,
      children
    } = this.props;

    return (
      <Placeholder.loading
        ready={allDucksReady}
        content={() => (
          <AsyncStripeProvider
            apiKey={customer.stripe_api_key}
            apiVersion={customer.stripe_api_version}
            enableBeta={customer.stripe_beta_enabled}
          >
            <InjectedForm {...this.props}>
              {children}
            </InjectedForm>
          </AsyncStripeProvider>
        )}
      />
    );
  }
}

/**
 * Bogus Stripe form for use when browser testing.  Doesn't use any third party dependencies,
 * but tries its best to work like a stripe form...
 */
class BogusStripeForm extends React.Component {
  handleSubmit(ev) {
    const {
      customer,
      onSubmit = ()=>{},
      beforeSubmit = ()=>{},
      onError = ()=>{},
      details = {},
      usesIntents = false,
    } = this.props;

    ev.preventDefault();

    beforeSubmit();

    onSubmit(
      {
        id: usesIntents ? DUMMY_TOKEN.id : customer.cc_payment_info.dummy_card_token,
      },
      {
        // Mock `stripe` for StripeCustomGateway
        stripe: {
          confirmCardSetup: (clientSecret, _data) => {
            if (clientSecret !== 'seti_mock_1_client_secret') {
              throw `invalid client secret: ${clientSecret}`;
            }

            return Promise.resolve({
              setupIntent: {
                id: 'seti_mock_1',
                payment_method: 'pm_mock_1'
              }
            });
          },

          confirmCardPayment: (clientSecret, _data) => {
            const resolution = {
              paymentIntent: {
                id: 'pi_mock_1',
                object: 'payment_intent',
                payment_method: 'pm_mock_1',
                status: 'succeeded',
                amount: '10'
              }
            };

            if (clientSecret !== 'pi_mock_1_client_secret') {
              resolution.error = { message: `invalid client secret: ${clientSecret}`, code: '124125' };
            }

            return Promise.resolve(resolution);
          },

          confirmSetup: (clientSecret, _data) => {
            if (clientSecret !== 'setti_mock_1_client_secret') {
              throw `invalid client secret: ${clientSecret}`
            }

            return Promise.resolve({
              setupIntent: {
                id: 'setti_mock_1',
                payment_method: 'pm_123'
              }
            })
          },

          confirmPayment: (clientSecret, _data) => {
            const resolution = {
              paymentIntent: {
                id: 'pi_mock_1',
                object: 'payment_intent',
                payment_method: 'pm_mock_1',
                status: 'succeeded',
                amount: '10'
              }
            }

            if (clientSecret !== 'pi_mock_1_client_secret') {
              resolution.error = { message: `invalid client secret: ${clientSecret}`, code: '12345'}
            }

            return Promise.resolve(resolution)
          },

          // Mock for confirming PADs PaymentIntent PreAuthorizedDebitInput.jsx
          confirmAcssDebitPayment: (clientSecret, _data) => {
            if (clientSecret !== 'pi_mock_2_client_secret') {
              throw `invalid client secret: ${clientSecret}`;
            }

            return Promise.resolve({
              paymentIntent: {
                id: 'pi_mock_2',
                object: 'payment_intent',
                payment_method: 'pm_mock_2',
                status: 'succeeded',
                amount: '10'
              }
            });
          },

          // Mock for confirming PADs SetupIntent PreAuthorizedDebitInput.jsx
          confirmAcssDebitSetup: (clientSecret, _data) => {
            if (clientSecret !== 'seti_mock_2_client_secret') {
              throw `invalid client secret: ${clientSecret}`;
            }

            return Promise.resolve({
              setupIntent: {
                id: 'seti_mock_2',
                payment_method: 'pm_mock_2'
              }
            });
          },

          collectUsBankAccountForPayment: (clientSecret, _data) => {
            if (clientSecret === 'pi_mock_1_client_secret') {
              return Promise.resolve({
                paymentIntent: {
                  id: 'pi_mock_1',
                  object: 'payment_intent',
                  payment_method: 'pm_mock_1',
                  status: 'requires_confirmation',
                  amount: '10'
                }
              });
            }

            throw `invalid client secret: ${clientSecret}`;
          },

          // Mock for confirming US Bank Account Debit PaymentIntent
          confirmUsBankAccountPayment: (clientSecret) => {
            if (clientSecret === 'pi_mock_1_client_secret') {
              return Promise.resolve({
                paymentIntent: {
                  id: 'pi_mock_1',
                  object: 'payment_intent',
                  payment_method: 'pm_mock_1',
                  status: 'processing',
                  amount: '10'
                }
              });
            }

            throw `invalid client secret: ${clientSecret}`;
          },

          // TODO
          collectUsBankAccountForSetup: (clientSecret, _data) => {
            if (clientSecret === 'seti_abandon_1_client_secret') {
              throw {
                message: "Please provide complete payment details.",
                code: "incomplete_payment_details"
              };
            }
            else if (clientSecret === "seti_blocked_1_client_secret") {
              throw {
                message: "Payment account blocked. Please contact Stripe support, not Finalsite Enrollment.",
                code: "payment_method_bank_account_blocked"
              };
            }
            else if (clientSecret === 'seti_mock_3_client_secret') {
              return Promise.resolve({
                setupIntent: {
                  id: 'seti_mock_3',
                  payment_method: 'pm_mock_3',
                  status: 'requires_confirmation'
                }
              });
            }
            else {
              throw `invalid client secret: ${clientSecret}`;
            }
          },

          // Mock for confirming US Bank Account Debit SetupIntent
          confirmUsBankAccountSetup: (clientSecret) => {
            if (clientSecret === 'seti_abandon_1_client_secret') {
              throw {
                message: "Please provide complete payment details.",
                code: "incomplete_payment_details"
              };
            } else if (clientSecret !== 'seti_mock_3_client_secret') {
              throw `invalid client secret: ${clientSecret}`;
            }

            return Promise.resolve({
              setupIntent: {
                id: 'seti_mock_3',
                payment_method: 'pm_mock_3',
                status: 'succeeded'
              }
            });
          },
        },

        // Mock `elements`
        elements: {
          getElement: () => ({})
        }
      },
    );
  }

  render() {
    const {customer} = this.props;

    return <form onSubmit={this.handleSubmit.bind(this)}>
      <div className={'alert alert-warning'}>
        The system is in test mode.  Payments will not be collected.
      </div>
      {this.props.children}
    </form>;
  }
}

class AsyncStripeProvider extends React.Component {
  static propTypes = {
    apiKey: PropTypes.string.isRequired,
    apiVersion: PropTypes.string.isRequired,
    enableBeta: PropTypes.bool
  };

  static defaultProps = {
    enableBeta: false
  };

  constructor() {
    super();
    this.state = {
      stripe: null
    };
  }

  // Have to fetch stripe's script form their servers for ...reasons
  // eslint-disable-next-line camelcase
  UNSAFE_componentWillMount() {
    const { apiKey, apiVersion, enableBeta } = this.props;

    requireScript('https://js.stripe.com/v3/?advancedFraudSignals=false', {
      unless: global.Stripe
    }).then(() => {
      const stripeOptions = enableBeta ? {
        betas: ['us_bank_account_beta_2'],
        apiVersion
      } : {};
      const stripe = window.Stripe(apiKey, stripeOptions);
      this.setState({ stripe });
    });
  }

  render(){
    return <StripeProvider stripe={this.state.stripe}>
      <Elements>
        {this.props.children}
      </Elements>
    </StripeProvider>;
  }
}

function mapDucksToProps(ownProps){
  return {
    customer: currentCustomer
  };
}

function mapStateToProps(state, ownProps){
  return {
  };
}

function mapDispatchToProps(dispatch, ownProps){
  return {
  };
}

export default duckify(mapDucksToProps, mapStateToProps, mapDispatchToProps)((props) => global.PAYMENTS_TEST_MODE ? <BogusStripeForm {...props}/> : <StripeForm {...props} />);
